// The create / edit airport form shown in the airports drawer (identity, location, status, FBO and operational data).
import type { FormInstance } from 'antd';
import { Alert, Checkbox, Form, Input, Select, Space, Typography } from 'antd';

import { COUNTRY_CODES, REGION_CODES, COUNTRY_DIALING } from '../../data/iso';
// SHAR-917 - numeric fields that refuse letters and symbols as they are typed.
import NumberInput from '../../components/NumberInput';
import { LIMITS } from '../../data/limits';
import { guessText } from '../../data/airportTime';

const { Text } = Typography;

// SHAR-1188 — the IANA zones the browser knows, for the time zone picker.
const TIME_ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();
const isTimeZone = (tz: string) => {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

export function AirportForm({ form, guessed = null }: { form: FormInstance; guessed?: string | null }) {
  return (
    <Form form={form} layout="vertical">
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: 'Required' }]}
      >
        {/* SHAR-205 — the API caps the name at 120; kept. */}
        <Input placeholder="London Gatwick" maxLength={LIMITS.airportName} />
      </Form.Item>
      <Space style={{ display: 'flex' }} align="start">
        <Form.Item
          name="icaoCode"
          label="ICAO"
          rules={[
            { required: true, message: 'Required' },
            // SHAR-460 — ICAO codes are 4 LETTERS only (no digits).
            { pattern: /^[A-Za-z]{4}$/, message: '4 letters' },
          ]}
        >
          <Input placeholder="EGKK" maxLength={4} style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item
          name="iataCode"
          label="IATA (optional)"
          rules={[{ pattern: /^[A-Za-z0-9]{3}$/, message: '3 characters' }]}
        >
          <Input placeholder="LGW" maxLength={3} style={{ textTransform: 'uppercase' }} />
        </Form.Item>
      </Space>
      <Space style={{ display: 'flex' }} align="start">
        <Form.Item
          name="countryCode"
          label="ISO country"
          rules={[
            { required: true, message: 'Required' },
            { pattern: /^[A-Za-z]{2}$/, message: '2 letters' },
            // SHAR-461 — reject reserved / user-assigned codes (e.g. "AA");
            // only real ISO 3166-1 alpha-2 countries are allowed.
            {
              validator: (_, value) =>
                !value || COUNTRY_CODES.has(String(value).toUpperCase())
                  ? Promise.resolve()
                  : Promise.reject(new Error('Not a valid ISO country code')),
            },
          ]}
        >
          <Input placeholder="GB" maxLength={2} style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item
          name="isoRegion"
          label="ISO region"
          // SHAR-474 — mandatory field.
          // SHAR-463 — re-validate the region when the ISO country changes
          // (e.g. GB-ENG entered, then country switched to US → now invalid).
          dependencies={['countryCode']}
          rules={[
            { required: true, message: 'Required' },
            { pattern: /^[A-Za-z]{2}-[A-Za-z0-9]{1,3}$/, message: 'e.g. GB-ENG' },
            // SHAR-462/463 — must be a real ISO 3166-2 subdivision AND belong to
            // the selected ISO country.
            ({ getFieldValue }) => ({
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const region = String(value).toUpperCase();
                const country = String(getFieldValue('countryCode') || '').toUpperCase();
                if (country && !region.startsWith(`${country}-`))
                  return Promise.reject(
                    new Error(`Region must belong to ${country} (e.g. ${country}-…)`),
                  );
                if (!REGION_CODES.has(region))
                  return Promise.reject(new Error('Not a valid ISO region code'));
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input placeholder="GB-ENG" style={{ textTransform: 'uppercase' }} />
        </Form.Item>
      </Space>
      <Form.Item
        name="city"
        label="Municipality"
        rules={[{ required: true, message: 'Required' }]}
      >
        <Input placeholder="London" maxLength={LIMITS.city} />
      </Form.Item>
      <Space style={{ display: 'flex' }} align="start">
        <Form.Item
          name="lat"
          label="Latitude"
          rules={[
            { required: true, message: 'Required' },
            { type: 'number', min: -90, max: 90, message: '-90..90' },
          ]}
        >
          {/* SHAR-917 (Copilot review) — the bounds are the FIELD's, and
              NumberInput reads `min` to decide whether a minus is typable.
              Without them a southern latitude could not be entered at all. */}
          <NumberInput min={-90} max={90} placeholder="51.15" step={0.01} precision={2} style={{ width: 160 }} />
        </Form.Item>
        <Form.Item
          name="lng"
          label="Longitude"
          rules={[
            { required: true, message: 'Required' },
            { type: 'number', min: -180, max: 180, message: '-180..180' },
          ]}
        >
          {/* As above — the placeholder itself is a western longitude. */}
          <NumberInput min={-180} max={180} placeholder="-0.19" step={0.01} precision={2} style={{ width: 160 }} />
        </Form.Item>
      </Space>
      {/* SHAR-1188 — departure and arrival times are local to the airport and
          every deadline is read in this zone. Left empty on a new airport, the
          API takes the zone of the nearest airport in the same country. */}
      <Form.Item
        name="timezone"
        label="Time zone"
        extra="IANA time zone, e.g. Europe/London. Left empty on a new airport, the nearest airport's zone is used and the airport goes on the guessed-zone list to check."
        rules={[
          {
            validator: (_, value) =>
              !value || isTimeZone(String(value))
                ? Promise.resolve()
                : Promise.reject(new Error('Not an IANA time zone')),
          },
        ]}
      >
        <Select
          showSearch
          allowClear
          placeholder="Europe/London"
          options={TIME_ZONES.map((z) => ({ value: z, label: z }))}
          filterOption={(input, opt) => String(opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>
      {/* SHAR-1192 — a guessed zone stays on the review list until someone
          confirms it here or picks another one. */}
      {guessed !== null ? (
        <>
          <Alert type="warning" showIcon style={{ marginBottom: 8 }} message={guessText(guessed)} />
          <Form.Item name="timezoneChecked" valuePropName="checked">
            <Checkbox>The time zone above is right</Checkbox>
          </Form.Item>
        </>
      ) : null}
      <Form.Item
        name="status"
        label="Status"
        rules={[{ required: true, message: 'Required' }]}
      >
        <Select
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      </Form.Item>

      <Text type="secondary">Optional operational data</Text>
      <div style={{ height: 8 }} />
      <Form.Item name="fboName" label="FBO name">
        <Input maxLength={LIMITS.name} />
      </Form.Item>
      <Form.Item name="fboAddress" label="FBO address">
        <Input maxLength={LIMITS.text} />
      </Form.Item>
      <Space style={{ display: 'flex' }} align="start">
        <Form.Item
          name="fboPhone"
          label="FBO phone"
          // SHAR-470 — validate an international phone (optional field): correct
          // format AND the country dialing code must match the selected ISO
          // country (e.g. under GB the number must start with +44).
          dependencies={['countryCode']}
          rules={[
            ({ getFieldValue }) => ({
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!/^\+?[\d][\d\s().-]{6,19}$/.test(String(value)))
                  return Promise.reject(
                    new Error('Enter a valid phone (e.g. +44 20 7946 0000)'),
                  );
                const country = String(getFieldValue('countryCode') || '').toUpperCase();
                const code = COUNTRY_DIALING[country];
                if (code) {
                  const digits = String(value).replace(/\D/g, '');
                  if (!digits.startsWith(code))
                    return Promise.reject(
                      new Error(`Phone must use the ${country} country code (+${code})`),
                    );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input placeholder="+44 20 7946 0000" maxLength={LIMITS.phone} />
        </Form.Item>
        <Form.Item name="fboEmail" label="FBO email" rules={[{ type: 'email', message: 'Invalid email' }]}>
          <Input maxLength={LIMITS.email} />
        </Form.Item>
      </Space>
      <Form.Item name="runwayLengthFt" label="Runway length (ft)">
        <NumberInput min={0} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="openingHours" label="Opening hours">
        <Input maxLength={LIMITS.text} placeholder="24h" />
      </Form.Item>
      <Form.Item name="generalNotes" label="General notes">
        <Input.TextArea maxLength={LIMITS.text} showCount rows={2} />
      </Form.Item>
    </Form>
  );
}
